
import { useState, useEffect } from 'react';
import { useSharePoint } from '@/context/SharePointContext';
import { toast } from 'sonner';
import sharePointService, { initialize, testConnection, getWebInfo } from '@/services/sharePointService';

type ConnectionStatusType = 'notConnected' | 'connecting' | 'connected' | 'error' | 'notConfigured';

export const useConnectionStatus = () => {
  const { 
    connectionStatus: contextConnectionStatus,
    siteUrl,
    setSiteUrl,
  } = useSharePoint();
  
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatusType>('notConnected');
  const [isConfiguring, setIsConfiguring] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{success: boolean, message: string, details?: any} | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkExistingAuth = async () => {
      try {
        console.log("Checking existing SharePoint authentication...");
        const isSharePointAuthenticated = localStorage.getItem("sharepoint_authenticated") === "true";
        
        if (isSharePointAuthenticated && siteUrl) {
          console.log("Found existing SharePoint authentication");
          
          // Initialize SharePoint service
          await initialize(siteUrl);
          
          // Test connection after a brief delay
          setTimeout(() => {
            handleTestConnection();
          }, 1000);
        } else {
          console.log("No existing SharePoint authentication found");
        }
      } catch (error) {
        console.error('Error checking existing SharePoint auth:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkExistingAuth();
  }, [siteUrl]);

  const handleConnect = async () => {
    if (isConfiguring) return;
    
    if (!siteUrl) {
      toast.error('SharePoint site URL is required', {
        description: 'Please configure the site URL first'
      });
      return;
    }
    
    setIsConfiguring(true);
    try {
      // Initialize SharePoint service
      await initialize(siteUrl);
      
      // Test the connection
      const isConnected = await testConnection();
      
      if (isConnected) {
        // Set a flag to indicate successful SharePoint connection
        localStorage.setItem("sharepoint_authenticated", "true");
        
        toast.success('Connected to Microsoft 365 services', {
          description: 'SharePoint connection established'
        });
        
        // Wait a bit before testing connection again
        setTimeout(() => {
          handleTestConnection();
        }, 1000);
      } else {
        throw new Error("Could not connect to SharePoint");
      }
    } catch (error) {
      console.error('Error connecting to SharePoint:', error);
      localStorage.removeItem("sharepoint_authenticated");
      toast.error('SharePoint connection error', {
        description: error instanceof Error ? error.message : 'Unknown error occurred'
      });
    } finally {
      setIsConfiguring(false);
    }
  };

  const handleTestConnection = async () => {
    if (isTesting) return;
    
    if (!siteUrl) {
      setTestResult({
        success: false,
        message: 'No SharePoint site URL configured'
      });
      toast.error('Connection test failed', {
        description: 'No SharePoint site URL configured'
      });
      return;
    }
    
    setIsTesting(true);
    setTestResult(null);
    
    try {
      console.log('Testing SharePoint connection with site URL:', siteUrl);
      
      // Make sure SharePoint service is initialized
      await initialize(siteUrl);
      
      // Test the connection
      const isConnected = await testConnection();
      
      if (isConnected) {
        // Get web info for more details
        const webInfo = await getWebInfo();
        
        if (webInfo) {
          setTestResult({
            success: true,
            message: `Connection verified successfully to ${webInfo.title}!`,
            details: webInfo
          });
          toast.success('Connection test successful', {
            description: `Connected to ${webInfo.title}`
          });
          setConnectionStatus('connected');
        } else {
          setTestResult({
            success: true,
            message: `Connection verified successfully!`,
            details: { url: siteUrl }
          });
          toast.success('Connection test successful', {
            description: `Connected to SharePoint`
          });
          setConnectionStatus('connected');
        }
      } else {
        setConnectionStatus('error');
        throw new Error('Could not connect to SharePoint');
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      setTestResult({
        success: false,
        message: `Connection test error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
      toast.error('Connection test error', {
        description: error instanceof Error ? error.message : 'Unknown error occurred'
      });
      setConnectionStatus('error');
    } finally {
      setIsTesting(false);
    }
  };

  return {
    connectionStatus,
    siteUrl,
    isConfiguring,
    isTesting,
    testResult,
    handleConnect,
    handleTestConnection,
    isLoading
  };
};

export default useConnectionStatus;
